import { Button, ButtonLink, IconSet, Tooltip } from '@postscript/components';
import { useHistory } from 'react-router-dom';

import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { useUser } from 'controllers/contexts/user';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import { createDraftFlowWithInfinityMessageAction } from 'components/flowBuilder/utils/flows';
import LaunchpadPromoCard from 'components/launchpad/LaunchpadPromoCard';
import ShopperDemoModal from 'components/shopper/components/modals/ShopperDemoModal';
import { TooltipPortal } from 'components/shopper/components/shared/TooltipPortal';
import ShopperAutomationsPromoImage from 'img/shopper-automations-promo.jpg';
import { logButtonClickEvent, PRODUCT_AREAS } from 'utils/events';

import {
  BRAND_CENTER_PATH,
  CONTENT_LIBRARY_PATH,
} from 'components/brand/constants';
import { FlowModes, FlowTypes } from 'components/flowBuilder/constants';
import { FlowWizardOptions } from 'components/flowBuilder/constants/trackingEvents';
import {
  SHOPPER_AUTOMATIONS_PATH,
  SHOPPER_CONFIG_BASE_PATH,
} from 'components/shopper/constants';
import {
  ACTIVATE_SHOPPER_AUTOMATIONS_STEP_TOOLTIP_ID,
  ACTIVATE_SHOPPER_STEP_KEY,
  BRAND_CENTER_SAVED_STEP_KEY,
  BUNDLED_AI_FEATURE_KEY,
  CONFIGURE_SHOPPER_STEP_KEY,
  CONTENT_LIBRARY_STEP_KEY,
  DEMO_SHOPPER_STEP_KEY,
  IT_AUTOMATION_STEP_KEY,
  IT_CAMPAIGN_STEP_KEY,
  PROGRESSIVE_ONBOARDING_SOURCE,
  SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';

import type {
  PromoCardSlotProps,
  WorkflowContent,
} from 'providers/ProgressiveOnboarding/types';

/**
 * Rollout promo card surfaced in the sidebar launcher when Shopper Automations
 * is the current (unblocked, incomplete) step -- i.e. the shop already has
 * Shopper Responses active. Swap ShopperAutomationsPromoImage if the art changes.
 */
const ShopperAutomationsPromoCard = ({ onDismiss }: PromoCardSlotProps) => {
  const { allGuides, setActiveGuide } = useProgressiveOnboarding();

  const handleLearnMore = () => {
    const bundledAiGuide = allGuides.find(
      (g) => g.key === BUNDLED_AI_FEATURE_KEY,
    );
    if (bundledAiGuide) setActiveGuide(bundledAiGuide);
  };

  return (
    <LaunchpadPromoCard
      title="Shopper Automations Is Now Available"
      description="Turn on Shopper Automations and let Shopper handle the triggers, test against your traditional flows, and see the results."
      tags={[
        { label: 'New Feature', variant: 'success' },
        { label: 'Beta', variant: 'info' },
      ]}
      onLearnMore={handleLearnMore}
      onDismiss={onDismiss}
      imageUrl={ShopperAutomationsPromoImage}
    />
  );
};

export const BUNDLED_AI_STEPS: WorkflowContent['steps'] = {
  [BRAND_CENTER_SAVED_STEP_KEY]: {
    key: BRAND_CENTER_SAVED_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Set Up Brand Guidelines',
    content:
      'Make sure your AI sounds like your brand before you demo or activate Shopper or AI tools.',
    icon: 'Sparkles',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={BRAND_CENTER_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: BRAND_CENTER_SAVED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Go To Brand Center
        </ButtonLink>
      );
    },
    subSteps: [
      'Pre-filled for quick setup',
      'Review and make edits if needed',
      "Save and you're set",
    ],
  },
  [CONTENT_LIBRARY_STEP_KEY]: {
    key: CONTENT_LIBRARY_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Fill Out Content Library',
    content:
      'Give Shopper the information it needs to answer questions accurately.',
    icon: 'Bookmark',
    subSteps: [
      'Upload FAQs, policies, care instructions and more as links or docs',
      'Preview how the content is being ingested',
      "Save and you're set",
    ],
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={CONTENT_LIBRARY_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: step.guideKey,
              step: step.key,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Go to Content Library
        </ButtonLink>
      );
    },
    SecondaryAction: ({ step }) => {
      const { user } = useUser();
      const { markStepCompleted } = useProgressiveOnboarding();

      if (step.state === 'incomplete') {
        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: step.guideKey,
                stepKey: step.key,
                metadata: { skipped: new Date().toISOString() },
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SHOPPER,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: step.guideKey,
                step: step.key,
                shop_id: user.shop_id,
              });
            }}
          >
            Skip for Now
          </Button>
        );
      }

      return null;
    },
  },
  [DEMO_SHOPPER_STEP_KEY]: {
    key: DEMO_SHOPPER_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Demo Shopper',
    content:
      'Text your custom Shopper and see how your AI responds before customers do.',
    icon: 'Iphone',
    PrimaryAction: ({ step }) => {
      const { showModal } = useGlobalModal();
      const { user } = useUser();

      return (
        <Button
          disabled={step.state === 'blocked'}
          onClick={(event) => {
            showModal({
              children: <ShopperDemoModal />,
              style: { width: 1070, padding: 0 },
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: DEMO_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Try Demo
        </Button>
      );
    },
    SecondaryAction: ({ step }) => {
      const { user } = useUser();
      const { markStepCompleted } = useProgressiveOnboarding();

      if (step.state === 'incomplete') {
        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: step.guideKey,
                stepKey: step.key,
                metadata: { skipped: new Date().toISOString() },
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SHOPPER,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: step.guideKey,
                step: step.key,
                shop_id: user.shop_id,
              });
            }}
          >
            Skip for Now
          </Button>
        );
      }

      return null;
    },
    subSteps: [
      'Try asking common product or support questions',
      'Test typos, weird phrasing, or off-topic messages',
      'Update Tone & Voice or Configure Shopper to adjust',
    ],
  },
  [CONFIGURE_SHOPPER_STEP_KEY]: {
    key: CONFIGURE_SHOPPER_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Configure Shopper',
    content:
      'Set up what Shopper says, how it responds, and what it should avoid.',
    icon: 'Settings2',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={SHOPPER_CONFIG_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: CONFIGURE_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Configure Shopper
        </ButtonLink>
      );
    },
    subSteps: [
      'Customize messages: add an offer, update FAQs, and review outreach and support details',
      "Manage content: exclude products Shopper shouldn't mention",
    ],
  },
  [ACTIVATE_SHOPPER_STEP_KEY]: {
    key: ACTIVATE_SHOPPER_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Activate Shopper Responses',
    content:
      'Turn on Shopper Responses so your AI can reply to customers in real time.',
    icon: 'Power',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={SHOPPER_CONFIG_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: ACTIVATE_SHOPPER_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Go To Activation
        </ButtonLink>
      );
    },
    subSteps: [
      'Click Activate to go live',
      'Start seeing real conversations and delivering faster responses to customers',
    ],
  },
  [SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY]: {
    key: SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Activate Shopper Automations',
    content:
      'Turn on Shopper Automations and let Shopper handle the triggers, test against your traditional flows, and see the results.',
    icon: 'GenerateMessage',
    PrimaryAction: ({ step }) => {
      const { user } = useUser();
      const { setActiveGuide } = useProgressiveOnboarding();

      return (
        <ButtonLink
          disabled={step.state === 'blocked'}
          to={SHOPPER_AUTOMATIONS_PATH}
          onClick={(event) => {
            // Close the onboarding drawer so the Automations tab is unobstructed
            // when the user lands on it.
            setActiveGuide(undefined);

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Set Up Automations
        </ButtonLink>
      );
    },
    SecondaryAction: ({ step }) => {
      const { user } = useUser();
      const { markStepCompleted } = useProgressiveOnboarding();

      if (step.state === 'incomplete') {
        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: step.guideKey,
                stepKey: step.key,
                metadata: { skipped: new Date().toISOString() },
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SHOPPER,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: step.guideKey,
                step: step.key,
                shop_id: user.shop_id,
              });
            }}
          >
            Skip for Now
          </Button>
        );
      }

      return null;
    },
    subSteps: [
      'Set the percentage of new subscribers Shopper messages',
      'Review sample messages for each trigger',
      'Activate to start driving revenue automatically',
    ],
    tooltip: (
      <TooltipPortal>
        <Tooltip id={ACTIVATE_SHOPPER_AUTOMATIONS_STEP_TOOLTIP_ID}>
          Turn on Shopper Responses first to unlock Shopper Automations.
        </Tooltip>
      </TooltipPortal>
    ),
    tooltipId: ACTIVATE_SHOPPER_AUTOMATIONS_STEP_TOOLTIP_ID,
    promoCard: ShopperAutomationsPromoCard,
  },
  [IT_CAMPAIGN_STEP_KEY]: {
    key: IT_CAMPAIGN_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Send Infinity Testing Campaign',
    content:
      'Send your first Infinity Testing Campaign to see how AI automatically finds your best-performing message.',
    icon: 'Send',
    PrimaryAction: ({ step }) => {
      const { push } = useHistory();
      const { user } = useUser();

      return (
        <Button
          disabled={step.state === 'blocked'}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: IT_CAMPAIGN_STEP_KEY,
              shop_id: user.shop_id,
            });

            const draftFlow = createDraftFlowWithInfinityMessageAction({
              mode: FlowModes.ADVANCED,
              type: FlowTypes.CAMPAIGN,
            });

            push('/campaigns/flows/new', {
              flow: draftFlow,
              from: FlowWizardOptions.INFINITY_TEST_CAMPAIGN,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Create Infinity Testing Campaign
        </Button>
      );
    },
    subSteps: [
      'Automatically spin up a new Campaign with Infinity Testing',
      'Let AI test variations and identify the top-performing content',
      'Track lift in EPM and revenue as results roll in',
    ],
  },
  [IT_AUTOMATION_STEP_KEY]: {
    key: IT_AUTOMATION_STEP_KEY,
    guideKey: BUNDLED_AI_FEATURE_KEY,
    title: 'Create Infinity Testing Automation',
    content:
      'Turn on Infinity Testing in your Automations to continuously optimize messaging without extra work.',
    icon: 'Sparkles',
    PrimaryAction: ({ step }) => {
      const { push } = useHistory();
      const { user } = useUser();

      return (
        <Button
          disabled={step.state === 'blocked'}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.SHOPPER,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: BUNDLED_AI_FEATURE_KEY,
              step: IT_AUTOMATION_STEP_KEY,
              shop_id: user.shop_id,
            });

            const draftFlow = createDraftFlowWithInfinityMessageAction({
              mode: FlowModes.ADVANCED,
              type: FlowTypes.AUTOMATION,
            });

            push('/automations/flows/new', {
              flow: draftFlow,
              from: FlowWizardOptions.AUTOMATION,
            });
          }}
          icon={IconSet.ArrowRight}
          iconPosition="right"
        >
          Create Infinity Testing Automation
        </Button>
      );
    },
    subSteps: [
      'Add an Infinity Test to an existing flow or build a new one',
      'AI explores message variations to improve conversion over time',
      'Review performance and lift directly from your Automation analytics',
    ],
  },
};
